"use client"

import { useState, useRef, useEffect } from "react"
import { useFBA, type AnalysisCase } from "@/context/functionalBehaviorAnalysisContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { FolderKanban, X, PlusCircle, LogOut, Home, MoreVertical } from "lucide-react"
import { getSupabaseClient } from "@/lib/supabaseClient"
import { useRouter } from "next/navigation"
import { cn } from "@/lib/utils"
import { CoinBalance } from "@/components/ui/coin-balance"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

// Safe utilities
const safeArray = <T,>(arr: T[] | undefined | null): T[] => arr || [];

export default function Sidebar() {
  const router = useRouter();
  const {
    cases,
    activeCaseId,
    setActiveCaseId,
    deleteAnalysisCase,
    addAnalysisCase,
    updateAnalysisCaseName,
  } = useFBA()
  
  // Use safe array to prevent undefined errors
  const safeCases = safeArray(cases)
  
  const { toast } = useToast()
  const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
  const [tempName, setTempName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCreateNewCase = () => {
    const newId = addAnalysisCase({}, "Nuevo caso");
    toast({
      title: "Nuevo caso creado",
      description: "Se ha creado un caso en blanco. Ahora puedes introducir los detalles.",
    });
  };

  const handleSignOut = async () => {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
    router.replace('/login');
  }

  const handleGoHome = () => {
    router.push('/');
  }

  const handleDoubleClick = (analysisCase: AnalysisCase) => {
    setEditingCaseId(analysisCase.id);
    setTempName(analysisCase.name);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempName(event.target.value);
  };

  const saveNameChange = () => {
    if (editingCaseId && tempName.trim() !== "") {
      updateAnalysisCaseName(editingCaseId, tempName.trim());
      toast({ title: "Caso renombrado", description: `Caso actualizado a "${tempName.trim()}".` });
    }
    setEditingCaseId(null);
    setTempName("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      saveNameChange();
    } else if (event.key === 'Escape') {
      setEditingCaseId(null);
      setTempName("");
    }
  };

  const handleBlur = () => {
    setTimeout(saveNameChange, 100);
  };

  useEffect(() => {
    if (editingCaseId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCaseId]);

  return (
    <Card className="w-72 flex flex-col border-r">
      {/* Navigation Bar */}
      <div className="border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <Button onClick={handleGoHome} size="sm" variant="ghost" className="h-8 px-2">
            <Home className="h-4 w-4 mr-2" />
            Inicio
          </Button>
          <div className="flex items-center gap-2">
            <CoinBalance size="sm" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Cases Section Header */}
      <div className="border-b px-4 py-3">
        <div>
          <h2 className="text-lg font-semibold">Mis casos</h2>
          <p className="text-sm text-muted-foreground">Crea y organiza tus casos</p>
        </div>
        <div className="mt-3">
          <Button onClick={handleCreateNewCase} size="sm" className="w-full">
            <PlusCircle className="h-4 w-4 mr-2" />
            Nuevo caso
          </Button>
        </div>
      </div>
      <CardContent className="pt-2">
        {safeCases.length > 0 ? (
          <ul className="space-y-1">
            {[...safeCases].sort((a, b) => b.createdAt - a.createdAt).map((analysisCase) => (
              <li
                key={analysisCase.id}
                className={cn(
                  "flex items-center justify-between text-sm p-2 rounded group cursor-pointer transition-opacity duration-200",
                  editingCaseId !== analysisCase.id && "hover:bg-muted",
                  activeCaseId === analysisCase.id && editingCaseId !== analysisCase.id && "bg-accent text-accent-foreground hover:bg-accent/90",
                  activeCaseId !== analysisCase.id && editingCaseId !== analysisCase.id && "opacity-70 hover:opacity-100"
                )}
                onClick={() => {
                  if (editingCaseId !== analysisCase.id) {
                    setActiveCaseId(analysisCase.id)
                  }
                }}
              >
                <div className="flex items-center overflow-hidden mr-2 flex-grow min-w-0">
                  <FolderKanban className="h-4 w-4 mr-2 flex-shrink-0" />
                  {editingCaseId === analysisCase.id ? (
                    <Input
                      ref={inputRef}
                      type="text"
                      value={tempName}
                      onChange={handleNameChange}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      className="h-6 px-1 text-sm flex-grow min-w-0"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      className="truncate flex-grow"
                      title={analysisCase.name}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleDoubleClick(analysisCase);
                      }}
                    >
                      {analysisCase.name}
                    </span>
                  )}
                </div>
                {editingCaseId !== analysisCase.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-6 w-6 flex-shrink-0",
                      "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                      activeCaseId === analysisCase.id && "opacity-100"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAnalysisCase(analysisCase.id);
                      toast({ title: "Caso eliminado", description: `Caso "${analysisCase.name}" eliminado.` });
                    }}
                    aria-label={`Eliminar caso ${analysisCase.name}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center pt-4 px-2">
            <p className="text-sm text-muted-foreground mb-4">Todavía no hay casos de análisis.</p>
            <Button onClick={handleCreateNewCase} variant="outline" className="w-full">
              <PlusCircle className="h-4 w-4 mr-2" />
              Crea tu primer caso
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
} 